import { useEffect, useMemo, useState } from "react";
import { Store, type KVStorage } from "../model/store";
import { ActionRunner, DataEngine, type RuntimeHooks } from "../controller/runtime";
import { NotEntitledError, type Transport } from "../controller/transport";
import type { Principal, SDUIDocument } from "../model/types";
import { SDUIProvider } from "./context";
import { RenderNode } from "./components";

export interface ScreenProps {
  doc: SDUIDocument;
  appId: string;
  transport: Transport;
  principal: Principal;
  storage?: { session?: KVStorage; local?: KVStorage };
  hooks?: RuntimeHooks;
  /** When false, the data engine is not started (used for deterministic SSR). */
  autoStart?: boolean;
}

/** Renders a fully-formed SDUI document: builds the store + runtime and provides context. */
export function Screen(props: ScreenProps) {
  const { doc, appId, transport, principal, storage, hooks, autoStart = true } = props;
  const rt = useMemo(() => {
    const store = new Store(doc.state, {
      user: principal.id, app: appId, screen: doc.screen,
      session: storage?.session, local: storage?.local,
    });
    const engine = new DataEngine(doc, store, transport, appId, hooks);
    const runner = new ActionRunner(doc, store, engine, transport, appId, hooks);
    return { store, engine, runner };
  }, [doc, appId, transport, principal.id, storage, hooks]);

  useEffect(() => {
    if (!autoStart) return;
    rt.engine.start();
    return () => rt.engine.dispose();
  }, [rt, autoStart]);

  return (
    <SDUIProvider value={{ store: rt.store, runner: rt.runner, appId, scope: {} }}>
      {doc.title ? <h2 className="sdui-screen-title">{doc.title}</h2> : null}
      <RenderNode node={doc.view} />
    </SDUIProvider>
  );
}

export interface ScreenLoaderProps {
  appId: string;
  screen: string;
  transport: Transport;
  principal: Principal;
  params?: Record<string, unknown>;
  storage?: { session?: KVStorage; local?: KVStorage };
  hooks?: RuntimeHooks;
  renderLoading?: () => React.ReactNode;
  renderError?: (err: unknown) => React.ReactNode;
  renderPurchase?: (app: string, purchaseUrl?: string) => React.ReactNode;
}

type LoadState =
  | { s: "loading" }
  | { s: "ready"; doc: SDUIDocument }
  | { s: "not_entitled"; app: string; purchaseUrl?: string }
  | { s: "error"; err: unknown };

/** App-level entry: fetches the SDUI document from the BFF, handles entitlement/loading/errors. */
export function ScreenLoader(props: ScreenLoaderProps) {
  const { appId, screen, transport, params } = props;
  const [state, setState] = useState<LoadState>({ s: "loading" });

  useEffect(() => {
    let live = true;
    setState({ s: "loading" });
    transport.sdui(appId, screen, params).then(
      (doc) => { if (live) setState({ s: "ready", doc }); },
      (err) => {
        if (!live) return;
        if (err instanceof NotEntitledError) setState({ s: "not_entitled", app: err.app, purchaseUrl: err.purchaseUrl });
        else setState({ s: "error", err });
      },
    );
    return () => { live = false; };
  }, [appId, screen, transport, JSON.stringify(params ?? {})]);

  if (state.s === "loading") return <>{props.renderLoading?.() ?? <div className="sdui-loading">Loading…</div>}</>;
  if (state.s === "not_entitled") return <>{props.renderPurchase?.(state.app, state.purchaseUrl) ?? <div className="sdui-purchase">구매가 필요합니다: {state.app}</div>}</>;
  if (state.s === "error") return <>{props.renderError?.(state.err) ?? <div className="sdui-error">오류가 발생했습니다</div>}</>;

  return (
    <Screen
      doc={state.doc}
      appId={appId}
      transport={transport}
      principal={props.principal}
      storage={props.storage}
      hooks={props.hooks}
    />
  );
}
